import React from "react";
import Dropzone from "react-dropzone";
import styled, { css } from "style/styled-components";
import strings from "localisation/strings";
import FieldErrors from "components/forms/FieldErrors";
import useNotificationState from "hooks/useNotification";
import useMutation from "common/hooks/useMutation";
import { uploadVideoBo } from "api/resources";
import ParamValidationError from "common/api/models/ParamValidationError";
import { dropZoneTestId } from "testing/testId";
import { ratioToValueParser } from "components/forms/FormFields/PictureGallery/helpers";
import { DragAndDropProps } from "components/forms/FormFields/AutoDragAndDrop";

type VideoAutoDragAndDropProps = Omit<
  DragAndDropProps,
  "slideErrors" | "setSlideErrors"
>;

interface BaseDropContainerProps {
  active?: boolean;
  hasErrors?: boolean;
  fileUrl?: string | null;
  height?: string;
  width?: string;
  fixedRatio?: boolean;
  isSmall: boolean;
  ratio: number;
}

export const DragAndDropContainer = styled.div<{ width?: string }>`
  margin-bottom: ${({ theme }) => theme.margin.medium};
  width: ${({ width }) => (width ? width : "100% ")};
`;

export const DropText = styled.div<{ hasValue?: boolean }>`
  text-align: center;
  max-width: 300px;
  margin: 0 auto;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  line-height: 18px;
  font-size: 15px;
  color: ${({ theme }) => theme.color.foreground.quinary};
  background-color: rgba(255, 255, 255, 0.8);
  border-radius: ${({ theme }) => theme.border.radius.medium};
  padding: ${({ theme }) => theme.margin.medium};
  visibility: ${({ hasValue }) => (hasValue ? "hidden" : "visible")};
`;

export const VideoContainer = styled.video`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

export const HintTooltipContainer = styled.div`
  position: absolute;
  top: 81%;
  left: 90%;
`;

export const Link = styled.div`
  color: ${({ theme }) => theme.color.background.secondary};
`;

export const SubText = styled.div<{ hasErrors?: boolean }>`
  font-size: ${({ theme }) => theme.font.size.small};
  white-space: nowrap;
  ${({ hasErrors, theme }) =>
    hasErrors &&
    `
    color: ${theme.color.background.secondary};
  `};
`;

// prettier-ignore
export const DropContainer = styled.div<BaseDropContainerProps>`
  ${() => ( videoContainerCss )}
  position: relative;
  outline: 0;
  cursor: pointer;

  :hover {
    ${DropText} {
      visibility: visible;
    }
  }
`;

const videoContainerCss = css<BaseDropContainerProps>`
  margin-top: ${({ theme }) => theme.margin.large};
  max-width: 100%;
  border-radius: 3px;
  padding-top: ${({ ratio }) => ratio}%;
  ${({ fixedRatio, height, width, ratio, fileUrl }) =>
    fixedRatio && !fileUrl
      ? `max-height: ${height ? height : "100%"};
    padding-top: ${ratio}%;`
      : `height: ${height ? height : "100%"};`}
  border: 1px dashed
    ${({ hasErrors, active, theme }) =>
      hasErrors || active ? theme.color.background.secondary : "#cbcbcb"};
`;

const VideoAutoDragAndDrop = ({
  fileUrl,
  errors,
  height,
  width,
  setFileUrl,
  parseErrors,
  setWaitingStatus,
  hasFormErrors,
  fixedRatio,
  isSmall = false,
  showHelpText = true,
  placeholder,
  showDistinctError,
  withCropAndZoom,
  onInitialUpload,
  setFileType,
  setFileName,
  ratio,
  fileMaxSize = 15,
  ...props
}: VideoAutoDragAndDropProps) => {
  const uploadVideo = uploadVideoBo;
  const { makeRequest } = useMutation(uploadVideo);

  const { addErrorNotification } = useNotificationState();

  // Extract video, send as FormData, get videoUrl back
  const onDrop = async (acceptedFiles: File[]) => {
    if (setWaitingStatus) setWaitingStatus(true);

    if (!acceptedFiles || acceptedFiles.length === 0) {
      addErrorNotification(strings("videoDragAndDrop.notAcceptableExtension"));
      if (setWaitingStatus) setWaitingStatus(false);
      return;
    }

    const file = acceptedFiles[0];

    if (file.size > fileMaxSize * 1024 * 1024) {
      addErrorNotification(
        strings("dragAndDrop.fileSize", { fileMaxSize: fileMaxSize }),
      );
      if (setWaitingStatus) setWaitingStatus(false);
      return;
    }

    try {
      const processedFile = file;
      const fileUrl = URL.createObjectURL(processedFile);

      if (setFileUrl) setFileUrl(fileUrl);
      if (setFileType) setFileType(file.type);
      if (setFileName) setFileName(file.name);
      if (onInitialUpload) onInitialUpload(fileUrl);

      const formData = new FormData();
      formData.append("file", processedFile, file.name);
      formData.append("container", props.container);
      const response = (await makeRequest(formData)) || {};

      if (parseErrors && response.error) parseErrors(response.error);
      if (response.error && showDistinctError) {
        addErrorNotification(response.error.detail);
      }
      if (response.error && response.error.paramsErrors) {
        const errorMessages: string[] = [];
        response.error.paramsErrors.forEach(
          (paramError: ParamValidationError) => {
            errorMessages.push(paramError.message);
          },
        );
      }

      const uploadedVideoUrl = (response.result && response.result.url) || null;
      if (setFileUrl) setFileUrl(uploadedVideoUrl);
      if (setWaitingStatus) setWaitingStatus(false);
    } catch (error) {
      console.error("File processing failed", error);
      if (setWaitingStatus) setWaitingStatus(false);
    }
  };

  return (
    <DragAndDropContainer width={width}>
      <Dropzone onDrop={onDrop} multiple={false} accept={["video/mp4"]}>
        {({ getRootProps, getInputProps, isDragActive }) => {
          return (
            <DropContainer
              {...getRootProps()}
              isSmall={isSmall}
              active={isDragActive}
              fileUrl={fileUrl}
              hasErrors={hasFormErrors}
              height={height}
              width={width}
              fixedRatio={fixedRatio}
              ratio={ratioToValueParser(ratio)}
              {...dropZoneTestId()}
            >
              <input {...getInputProps()} />
              {fileUrl && (
                <VideoContainer
                  src={fileUrl}
                  controls
                  width="100%"
                  height="100%"
                />
              )}
              <DropText hasValue={!!fileUrl}>
                {isDragActive ? (
                  <Link>{strings(`videoDragAndDrop.dropHere`)}</Link>
                ) : (
                  <>
                    {placeholder && <Link>{placeholder}</Link>}
                    {!isSmall &&
                      showHelpText &&
                      strings(`videoDragAndDrop.orDrop`)}
                  </>
                )}
              </DropText>
            </DropContainer>
          );
        }}
      </Dropzone>
      <FieldErrors errors={errors} />
    </DragAndDropContainer>
  );
};

export default VideoAutoDragAndDrop;
